import { useEffect } from 'react';
import type { CaseItem } from '../../mocks/cases';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://rwa99.cn';

interface CaseDetailSEOHeadProps {
  caseItem: CaseItem;
}

export default function CaseDetailSEOHead({ caseItem }: CaseDetailSEOHeadProps) {
  useEffect(() => {
    if (!caseItem) return;

    const pageUrl = `${SITE_URL}/cases/${caseItem.id}`;
    const descText = `${caseItem.name} — ${caseItem.desc} 发行地区：${caseItem.region}，平台：${caseItem.platform}，规模：${caseItem.tvl}，代币标准：${caseItem.standard || 'ERC-20'}，状态：${caseItem.status}。`;

    document.title = `${caseItem.name} | RWA99案例深度库 — ${caseItem.type}赛道`;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta('description', descText.slice(0, 160));
    setMeta('keywords', `${caseItem.name},${caseItem.type}RWA,${caseItem.platform},${caseItem.region}RWA案例,RWA代币化,${caseItem.standard || 'ERC-20'},RWA99`);
    setMeta('robots', 'index, follow');
    setMeta('og:title', `${caseItem.name} | RWA99案例深度库`, true);
    setMeta('og:description', `${caseItem.type}赛道 · ${caseItem.region} · ${caseItem.tvl} · ${caseItem.status}${caseItem.contractCode ? ' · 含智能合约源码' : ''}`, true);
    setMeta('og:type', 'article', true);
    setMeta('og:url', pageUrl, true);
    setMeta('og:site_name', 'RWA99.cn', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', `${caseItem.name} | RWA99案例`);
    setMeta('twitter:description', `${caseItem.type}赛道 · ${caseItem.region} · ${caseItem.tvl} · ${caseItem.status}`);

    const existing = document.querySelectorAll('script[data-case-detail-jsonld]');
    existing.forEach((el) => el.remove());

    const schemas: object[] = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: `${caseItem.name} | RWA99案例深度库`,
        description: descText.slice(0, 300),
        inLanguage: 'zh-CN',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        publisher: { '@id': `${SITE_URL}/#organization` },
        datePublished: caseItem.launchDate || '2026-01-01',
        dateModified: '2026-03-30',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: '案例深度库', item: `${SITE_URL}/cases` },
            { '@type': 'ListItem', position: 3, name: caseItem.name, item: pageUrl },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': `${pageUrl}#article`,
        url: pageUrl,
        headline: caseItem.name,
        description: caseItem.detail || caseItem.desc,
        inLanguage: 'zh-CN',
        author: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'RWA99.cn' },
        publisher: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'RWA99.cn', url: SITE_URL },
        datePublished: caseItem.launchDate || '2026-01-01',
        dateModified: '2026-03-30',
        isPartOf: { '@id': `${SITE_URL}/cases#collection` },
        about: [
          { '@type': 'Thing', name: `${caseItem.type}代币化` },
          { '@type': 'Thing', name: caseItem.platform },
          { '@type': 'Thing', name: caseItem.region },
          ...(caseItem.standard ? [{ '@type': 'Thing', name: caseItem.standard }] : []),
        ],
        keywords: [caseItem.name, `${caseItem.type}RWA`, caseItem.platform, caseItem.region, 'RWA代币化', caseItem.standard || 'ERC-20'],
        ...(caseItem.highlights && caseItem.highlights.length > 0 ? {
          hasPart: caseItem.highlights.map((h) => ({
            '@type': 'WebPageElement',
            name: h,
          })),
        } : {}),
      },
    ];

    // Add SoftwareSourceCode schema if contract code exists
    if (caseItem.contractCode) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'SoftwareSourceCode',
        '@id': `${pageUrl}#contract`,
        name: `${caseItem.name} 智能合约源码`,
        description: `${caseItem.name}的Solidity智能合约示例源码，代币标准：${caseItem.standard || 'ERC-20'}`,
        programmingLanguage: {
          '@type': 'ComputerLanguage',
          name: 'Solidity',
        },
        codeRepository: caseItem.contractAddress
          ? `https://etherscan.io/token/${caseItem.contractAddress}`
          : `${SITE_URL}/cases/${caseItem.id}`,
        targetProduct: {
          '@type': 'SoftwareApplication',
          name: 'Ethereum Smart Contract',
          operatingSystem: 'Ethereum Mainnet',
          applicationCategory: 'DeFi / RWA',
        },
        author: { '@type': 'Organization', name: caseItem.issuer || caseItem.name },
        isPartOf: { '@id': `${pageUrl}#article` },
      });
    }

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-case-detail-jsonld', 'true');
      script.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-case-detail-jsonld]').forEach((el) => el.remove());
    };
  }, [caseItem]);

  return null;
}
